"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCoachMarkers } from "@/hooks/useCoachMarkers";
import { cn } from "@/lib/utils";

interface CoachMarkerProps {
  id: string;
  title: string;
  description: string;
  children: React.ReactNode;
  placement?: "left" | "top" | "bottom" | "right";
  step?: number;
  totalSteps?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  nextLabel?: string;
  previousLabel?: string;
  className?: string;
}

// Gradient color combinations for background patterns
const gradientCombinations = [
  { from: "from-purple-400/40", to: "to-indigo-500/60" },
  { from: "from-green-400/40", to: "to-blue-500/60" },
  { from: "from-pink-400/40", to: "to-rose-500/60" },
  { from: "from-cyan-400/40", to: "to-teal-500/60" },
  { from: "from-amber-400/40", to: "to-orange-500/60" },
  { from: "from-violet-400/40", to: "to-purple-500/60" },
  { from: "from-emerald-400/40", to: "to-green-500/60" },
  { from: "from-blue-400/40", to: "to-cyan-500/60" },
  { from: "from-red-400/40", to: "to-pink-500/60" },
  { from: "from-indigo-400/40", to: "to-blue-500/60" },
  { from: "from-lime-400/40", to: "to-emerald-500/60" },
  { from: "from-fuchsia-400/40", to: "to-purple-500/60" },
  { from: "from-sky-400/40", to: "to-blue-500/60" },
  { from: "from-yellow-400/40", to: "to-amber-500/60" },
  { from: "from-teal-400/40", to: "to-cyan-500/60" },
];

export const CoachMarker = ({
  id,
  title,
  description,
  children,
  placement = "left",
  step,
  totalSteps,
  onNext,
  onPrevious,
  nextLabel,
  previousLabel,
  className,
}: CoachMarkerProps) => {
  const { t } = useTranslation(["common", "help"]);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerPos, setTriggerPos] = useState<DOMRect | null>(null);
  const [showSkipConfirmDialog, setShowSkipConfirmDialog] = useState(false);
  const [backgroundConfig] = useState(() => {
    // Pick two different random gradient colors
    const availableGradients = [...gradientCombinations];
    const firstIndex = Math.floor(Math.random() * availableGradients.length);
    const firstGradient = availableGradients[firstIndex];

    // Remove the selected gradient to ensure they're different
    availableGradients.splice(firstIndex, 1);
    const secondIndex = Math.floor(Math.random() * availableGradients.length);
    const secondGradient = availableGradients[secondIndex];

    // Randomly choose positioning: true = top-left/bottom-right, false = top-right/bottom-left
    const isLeftRight = Math.random() > 0.5;

    return {
      circle1: firstGradient,
      circle2: secondGradient,
      positioning: isLeftRight ? "left-right" : "right-left",
    };
  });

  const {
    registerMarker,
    unregisterMarker,
    showNextMarker,
    showPreviousMarker,
    shouldShowMarker,
    isMarkerOpen,
    skipAllMarkers,
    setEnabled,
    closeMarker,
  } = useCoachMarkers();

  const isOpen = isMarkerOpen(id);
  const shouldShow = shouldShowMarker(id);

  const [currentSide, setCurrentSide] = useState<"left" | "right" | "bottom" | "top">(placement);

  // Function to update trigger position
  const updateTriggerPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      setTriggerPos(rect);
    }
  }, []);

  // Custom collision detection function that also updates position
  const findBestSide = useCallback(() => {
    if (!triggerRef.current) return placement;

    const trigger = triggerRef.current;
    const rect = trigger.getBoundingClientRect();

    // Always update the trigger position when calculating best side
    setTriggerPos(rect);

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    const popoverWidth = 384; // w-96 = 384px
    const popoverHeight = 250; // estimated height with padding
    const padding = 16;

    // Calculate available space for each side
    const spaceLeft = rect.left;
    const spaceRight = viewport.width - rect.right;
    const spaceTop = rect.top;
    const spaceBottom = viewport.height - rect.bottom;

    // Check if the preferred side (placement) has enough space
    const preferredSideSpace = (() => {
      switch (placement) {
        case "left":
          return spaceLeft;
        case "right":
          return spaceRight;
        case "top":
          return spaceTop;
        case "bottom":
          return spaceBottom;
        default:
          return 0;
      }
    })();

    const preferredSideRequired = (() => {
      switch (placement) {
        case "left":
        case "right":
          return popoverWidth + padding;
        case "top":
        case "bottom":
          return popoverHeight + padding;
        default:
          return 0;
      }
    })();

    // If the preferred side has enough space, use it
    if (preferredSideSpace >= preferredSideRequired) {
      console.log(
        `CoachMarker: Using preferred side ${placement} (space: ${preferredSideSpace}, required: ${preferredSideRequired})`,
      );

      return placement;
    }

    // If preferred side doesn't fit, fall back to collision detection
    const sides: Array<{
      side: "left" | "right" | "bottom" | "top";
      space: number;
      required: number;
    }> = [
      { side: "left", space: spaceLeft, required: popoverWidth + padding },
      { side: "right", space: spaceRight, required: popoverWidth + padding },
      { side: "bottom", space: spaceBottom, required: popoverHeight + padding },
      { side: "top", space: spaceTop, required: popoverHeight + padding },
    ];

    // Find the first side that fits
    for (const { side, space, required } of sides) {
      if (space >= required) {
        console.log(`CoachMarker: Selected side ${side} (space: ${space}, required: ${required})`);

        return side;
      }
    }

    // If nothing fits, choose the side with the most space
    const bestFit = sides.reduce((best, current) => (current.space > best.space ? current : best));

    console.log(
      `CoachMarker: No side fits perfectly, choosing ${bestFit.side} with most space (${bestFit.space})`,
    );

    return bestFit.side;
  }, [placement]);

  useEffect(() => {
    if (!shouldShow && document.body.style.pointerEvents === "none") {
      document.body.style.pointerEvents = "auto";
    }
  }, [shouldShow]);

  // Update side when popover opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        const bestSide = findBestSide();

        setCurrentSide(bestSide);
        // Position is already updated in findBestSide
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, findBestSide]);

  // Handle window resize and content changes
  useLayoutEffect(() => {
    if (!isOpen) return;

    let resizeObserver: ResizeObserver | null = null;
    let animationFrameId: number | null = null;

    const handleUpdate = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const bestSide = findBestSide();

        setCurrentSide(bestSide);
        // Position is already updated in findBestSide
        animationFrameId = null;
      });
    };

    // Handle window resize (including DevTools opening/closing)
    const handleResize = () => {
      handleUpdate();
    };

    // Handle scroll events that might change element position
    const handleScroll = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        updateTriggerPosition();
        animationFrameId = null;
      });
    };

    // Set up ResizeObserver to watch for element size changes
    if (triggerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        handleUpdate();
      });

      // Observe the trigger element
      resizeObserver.observe(triggerRef.current);

      // Also observe the document body for overall layout changes
      resizeObserver.observe(document.body);
    }

    // Add event listeners
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true); // Use capture to catch all scroll events
    document.addEventListener("scroll", handleScroll, true);

    // Initial update
    handleUpdate();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("scroll", handleScroll, true);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isOpen, findBestSide, updateTriggerPosition]);

  // Additional effect to ensure position is always up-to-date
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      // Set up an interval to periodically check position
      const intervalId = setInterval(() => {
        updateTriggerPosition();
      }, 100); // Check every 100ms when open

      return () => clearInterval(intervalId);
    }
  }, [isOpen, updateTriggerPosition]);

  // Auto-scroll to element when marker opens
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      // Small delay to allow the popover to render first
      const timeoutId = setTimeout(() => {
        triggerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  // Get circle positioning classes based on random configuration
  const getCirclePositions = () => {
    if (backgroundConfig.positioning === "left-right") {
      return {
        circle1: "absolute -top-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br",
        circle2: "absolute -bottom-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-tr",
      };
    } else {
      return {
        circle1: "absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-bl",
        circle2: "absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tl",
      };
    }
  };

  // Register this marker when component mounts
  useEffect(() => {
    if (!shouldShow) return;

    // Use the trigger element for registration
    const element = triggerRef.current || document.createElement("div");

    registerMarker({
      id,
      title,
      description,
      placement,
      step,
      totalSteps,
      nextLabel,
      previousLabel,
      element,
      onNext,
      onPrevious,
    });

    return () => {
      unregisterMarker(id);
    };
  }, [
    id,
    title,
    description,
    placement,
    step,
    totalSteps,
    nextLabel,
    previousLabel,
    onNext,
    onPrevious,
    registerMarker,
    unregisterMarker,
    shouldShow,
  ]);

  const handleNext = useCallback(() => {
    showNextMarker();
  }, [showNextMarker]);

  const handlePrevious = useCallback(() => {
    showPreviousMarker();
  }, [showPreviousMarker]);

  const handleSkipAllClick = useCallback(() => {
    // Close the coach marker popover first
    closeMarker();
    // Then show the confirmation dialog
    setShowSkipConfirmDialog(true);
  }, [closeMarker]);

  const handleSkipAllConfirm = useCallback(() => {
    skipAllMarkers();
    setEnabled(false); // Disable coach markers completely
    setShowSkipConfirmDialog(false);
  }, [skipAllMarkers, setEnabled]);

  const handleSkipAllCancel = useCallback(() => {
    setShowSkipConfirmDialog(false);
  }, []);

  // Render content
  const renderContent = () => {
    const circlePositions = getCirclePositions();

    return (
      <motion.div
        animate={{ opacity: 1, scale: 1 }}
        className="relative overflow-hidden p-6"
        initial={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {/* Background Pattern with Random Colors and Positions */}
        <div className="absolute inset-0 overflow-hidden opacity-5">
          <div
            className={cn(
              circlePositions.circle1,
              backgroundConfig.circle1.from,
              backgroundConfig.circle1.to,
            )}
          />
          <div
            className={cn(
              circlePositions.circle2,
              backgroundConfig.circle2.from,
              backgroundConfig.circle2.to,
            )}
          />
        </div>

        {/* Header */}
        <div className="relative z-10 mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-accent-foreground/10 flex h-10 w-10 items-center justify-center rounded-xl backdrop-blur-sm">
              <Icon
                className="text-accent-foreground"
                height="1.25rem"
                icon="lucide:compass"
                width="1.25rem"
              />
            </div>
            <div>
              <h3 className="text-accent-foreground text-lg font-semibold">{title}</h3>
              {step && totalSteps && (
                <div className="text-muted-foreground flex items-center gap-1 text-xs">
                  <span>Step</span>
                  <span>{step}</span>
                  <span>/</span>
                  <span>{totalSteps}</span>
                </div>
              )}
            </div>
          </div>

          {/* Skip All Button */}
          <TooltipProvider>
            <Tooltip defaultOpen={false}>
              <TooltipTrigger asChild>
                <Button
                  className="bg-muted hover:bg-muted/80 h-8 px-3 text-xs"
                  size="sm"
                  onClick={handleSkipAllClick}
                >
                  <Icon
                    className="mr-1"
                    height="0.875rem"
                    icon="lucide:skip-forward"
                    width="0.875rem"
                  />
                  {t("help:coachMarkers.skipAll.button", { defaultValue: "Skip all" })}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>
                  {t("help:coachMarkers.skipAll.tooltip", {
                    defaultValue: "Skip all remaining coach markers on this page",
                  })}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Content */}
        <div className="relative z-10 mb-6">
          <p className="text-accent-foreground/80 text-sm leading-relaxed">{description}</p>
        </div>

        {/* Actions */}
        <div className="relative z-10 flex flex-col gap-3">
          <div className="flex gap-2">
            {onPrevious && (
              <Button
                className="bg-muted/20 hover:bg-muted/30 border-border/50 flex-1 rounded-xl"
                size="sm"
                variant="outline"
                onClick={handlePrevious}
              >
                <Icon
                  className="mr-2"
                  height="0.875rem"
                  icon="lucide:arrow-left"
                  width="0.875rem"
                />
                {previousLabel || t("common:back", { defaultValue: "Previous" })}
              </Button>
            )}
            <Button
              className="bg-accent-foreground text-primary hover:bg-accent-foreground/80 flex-1 rounded-xl"
              size="sm"
              onClick={handleNext}
            >
              {nextLabel ||
                (step === totalSteps
                  ? t("help:coachMarkers.gotIt", { defaultValue: "Got it!" })
                  : t("common:next", { defaultValue: "Next" }))}
              {step !== totalSteps && (
                <Icon
                  className="ml-2"
                  height="0.875rem"
                  icon="lucide:arrow-right"
                  width="0.875rem"
                />
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <Popover modal={true} open={isOpen && shouldShow}>
        {isOpen &&
          shouldShow &&
          createPortal(
            <div className={"fixed top-0 left-0 z-[4999] h-screen w-screen bg-black/50"} />,
            document.body,
          )}
        {isOpen &&
          shouldShow &&
          triggerPos &&
          createPortal(
            <div
              style={{
                position: "fixed",
                overflow: "hidden",
                top: triggerPos.top,
                left: triggerPos.left,
                width: triggerPos.width,
                height: triggerPos.height,
                zIndex: 5000,
                pointerEvents: "none",
              }}
            >
              <motion.div
                animate={{ opacity: 1 }}
                className="coach-marker-portal"
                initial={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
              >
                {children}
              </motion.div>
            </div>,
            document.body,
          )}
        <PopoverTrigger asChild>
          <div
            ref={triggerRef}
            className={cn("relative", className, isOpen && shouldShow && "z-[5000]")}
          >
            {children}
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          className="bg-side-accent/80 text-accent-foreground z-[5001] w-96 max-w-[max(var(--radix-popover-trigger-width),_75dvw))] border-none p-0 shadow-2xl backdrop-blur-xl"
          side={currentSide}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <PopoverArrow className="fill-side-accent/80 h-4 w-8 backdrop-blur-xl" />
          <ScrollArea className="max-h-[400px]">{renderContent()}</ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Skip All Confirmation Dialog */}
      <AlertDialog open={showSkipConfirmDialog} onOpenChange={setShowSkipConfirmDialog}>
        <AlertDialogContent className="bg-side-accent/80 text-accent-foreground border-none backdrop-blur-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("help:coachMarkers.skipAll.confirmDialog.title", {
                defaultValue: "Skip Tutorial",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("help:coachMarkers.skipAll.confirmDialog.description", {
                defaultValue:
                  "Are you sure you want to skip the tutorial? You can always re-enable coach markers in your settings later.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleSkipAllCancel}>
              {t("help:coachMarkers.skipAll.confirmDialog.cancel", {
                defaultValue: "Continue Tutorial",
              })}
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                "bg-accent-foreground text-primary hover:text-background hover:bg-accent-foreground/75"
              }
              onClick={handleSkipAllConfirm}
            >
              {t("help:coachMarkers.skipAll.confirmDialog.confirm", {
                defaultValue: "Skip Tutorial",
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
